import {
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  Check,
  CheckCheck,
  ChefHat,
  Clock3,
  Loader2,
  RefreshCw,
  ShoppingBag,
  Sparkles,
  Truck,
  XCircle,
} from 'lucide-react';

import notificationsApi from '../../api/notificationsApi';
import ordersApi from '../../api/ordersApi';

import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';

import '../../styles/Notifications/NotificationsPage.css';

const DEFAULT_ESTABLISHMENT_ID = 1;

const ESTABLISHMENT_STORAGE_KEY =
  'selected_establishment_id';

const MAX_NOTIFICATIONS = 50;

const MANAGER_ROLES = [
  'restaurantmanager',
  'restaurantmanagerrole',
  'manager',
  'admin',
  'administrator',
  'superadmin',
  'superadministrator',
];

const WAITER_ROLES = [
  'waiter',
  'serveur',
];

const KITCHEN_ROLES = [
  'cuisine',
  'kitchen',
  'cuisiner',
  'cuisinier',
];

const ORDER_STATUS_KEYS = [
  'pending',
  'accepted',
  'preparing',
  'ready',
  'delivered',
  'cancelled',
];

/*
 * Keep exactly the same order icons used
 * by RoleNotifications.jsx.
 */
const statusIcons = {
  pending: Clock3,
  accepted: Check,
  preparing: ChefHat,
  ready: Truck,
  delivered: CheckCheck,
  cancelled: XCircle,
};

const translateFallback = (
  t,
  key,
  fallback,
) => {
  const value = t(key);

  return value === key
    ? fallback
    : value;
};

const normalizeRole = (role) => {
  return String(role || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, '');
};

const normalizeStatus = (status) => {
  const value = String(
    status || '',
  )
    .trim()
    .toLowerCase();

  return ORDER_STATUS_KEYS.includes(
    value,
  )
    ? value
    : 'pending';
};

const getUserWorkflow = (user) => {
  const role = normalizeRole(
    user?.role ||
      user?.type ||
      user?.profile?.role,
  );

  if (
    WAITER_ROLES.includes(role)
  ) {
    return 'waiter';
  }

  if (
    KITCHEN_ROLES.includes(role)
  ) {
    return 'kitchen';
  }

  if (
    MANAGER_ROLES.includes(role)
  ) {
    return 'manager';
  }

  return 'manager';
};

const getVisibleStatusesByWorkflow = (
  workflow,
) => {
  if (workflow === 'waiter') {
    return [
      'pending',
      'ready',
    ];
  }

  if (workflow === 'kitchen') {
    return [
      'accepted',
      'preparing',
    ];
  }

  return ORDER_STATUS_KEYS;
};

const getOrderReference = (
  order,
  t,
) => {
  if (!order) {
    return '';
  }

  const orderNumber =
    order.order_number ||
    `#${order.id}`;

  const tableNumber =
    order.table_number
      ? `${translateFallback(
          t,
          'orders.table.tableNumber',
          'Table',
        )} ${order.table_number}`
      : '';

  const customerName =
    order.customer_name &&
    order.customer_name !== '-'
      ? order.customer_name
      : '';

  return [
    orderNumber,
    tableNumber ||
      customerName,
  ]
    .filter(Boolean)
    .join(' · ');
};

const buildOrderTitle = (
  t,
  order,
) => {
  const status =
    normalizeStatus(
      order?.status,
    );

  if (status === 'pending') {
    return translateFallback(
      t,
      'orders.notifications.created',
      'New order received',
    );
  }

  return translateFallback(
    t,
    `orders.notifications.status.${status}`,
    translateFallback(
      t,
      `orders.status.${status}`,
      status,
    ),
  );
};

const buildOrderMessage = (
  t,
  order,
) => {
  const reference =
    getOrderReference(
      order,
      t,
    );

  const defaultMessage =
    translateFallback(
      t,
      'orders.notifications.defaultMessage',
      'Open the order to view details.',
    );

  if (!reference) {
    return defaultMessage;
  }

  return `${reference} · ${defaultMessage}`;
};

const getSavedOrderNotifications = (
  storageKey,
) => {
  try {
    const stored =
      localStorage.getItem(
        storageKey,
      );

    if (!stored) {
      return [];
    }

    const parsed =
      JSON.parse(stored);

    return Array.isArray(parsed)
      ? parsed
      : [];
  } catch {
    return [];
  }
};

const extractApiList = (
  response,
) => {
  const possibilities = [
    response?.data?.data?.data,
    response?.data?.data,
    response?.data?.items,
    response?.data,
  ];

  return (
    possibilities.find(
      (item) =>
        Array.isArray(item),
    ) || []
  );
};

export default function NotificationsPage() {
  const navigate =
    useNavigate();

  const {
    t,
    language,
  } = useLanguage();

  const {
    user,
  } = useAuth();

  const [
    notifications,
    setNotifications,
  ] = useState([]);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    refreshing,
    setRefreshing,
  ] = useState(false);

  const [
    filter,
    setFilter,
  ] = useState('all');

  const userWorkflow =
    useMemo(
      () =>
        getUserWorkflow(user),
      [user],
    );

  const visibleStatuses =
    useMemo(
      () =>
        getVisibleStatusesByWorkflow(
          userWorkflow,
        ),
      [userWorkflow],
    );

  const establishmentId =
    localStorage.getItem(
      ESTABLISHMENT_STORAGE_KEY,
    ) ||
    DEFAULT_ESTABLISHMENT_ID;

  const orderStorageKey =
    `role_order_notifications_${establishmentId}_${userWorkflow}`;

  const filters = [
    {
      key: 'all',
      label: t(
        'notifications.filters.all',
      ),
    },
    {
      key: 'unread',
      label: t(
        'notifications.filters.unread',
      ),
    },
    {
      key: 'qr',
      label: t(
        'notifications.filters.qr',
      ),
    },
  ];

  const formatDateTime = (
    value,
  ) => {
    if (!value) {
      return '';
    }

    try {
      return new Intl.DateTimeFormat(
        language,
        {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        },
      ).format(
        new Date(value),
      );
    } catch {
      return value;
    }
  };

  const saveOrderReadState = (
    nextNotifications,
  ) => {
    const orderNotifications =
      nextNotifications
        .filter(
          (notification) =>
            notification.source ===
            'order',
        )
        .map(
          (notification) => ({
            id: notification.id,
            order:
              notification.order,
            order_id:
              notification.order_id,
            status:
              notification.status,
            title:
              notification.title,
            message:
              notification.message,
            link:
              notification.link,
            created_at:
              notification.created_at,
            read:
              notification.read,
          }),
        )
        .slice(
          0,
          MAX_NOTIFICATIONS,
        );

    localStorage.setItem(
      orderStorageKey,
      JSON.stringify(
        orderNotifications,
      ),
    );
  };

  const buildOrderNotifications = (
    orders,
  ) => {
    const savedNotifications =
      getSavedOrderNotifications(
        orderStorageKey,
      );

    const savedReadState =
      savedNotifications.reduce(
        (
          result,
          notification,
        ) => {
          const orderId =
            notification.order_id ||
            notification?.order?.id;

          if (orderId) {
            result[orderId] =
              Boolean(
                notification.read,
              );
          }

          return result;
        },
        {},
      );

    return orders
      .filter((order) => {
        if (
          userWorkflow ===
          'manager'
        ) {
          return true;
        }

        return visibleStatuses.includes(
          normalizeStatus(
            order.status,
          ),
        );
      })
      .slice(
        0,
        MAX_NOTIFICATIONS,
      )
      .map((order) => {
        const status =
          normalizeStatus(
            order.status,
          );

        return {
          id: `order-${order.id}`,
          source: 'order',
          type: 'order',
          order,
          order_id: order.id,
          status,
          title:
            buildOrderTitle(
              t,
              order,
            ),
          message:
            buildOrderMessage(
              t,
              order,
            ),
          link: `/orders/${order.id}`,
          created_at:
            order.updated_at ||
            order.created_at,
          read:
            savedReadState[
              order.id
            ] || false,
        };
      });
  };

  const fetchNotifications =
    async (
      silent = false,
    ) => {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      try {
        const [
          platformResult,
          ordersResult,
        ] =
          await Promise.allSettled([
            notificationsApi.getNotifications(),

            ordersApi.getOrders(
              establishmentId,
              {
                per_page:
                  MAX_NOTIFICATIONS,
              },
            ),
          ]);

        let platformNotifications =
          [];

        let orderNotifications =
          [];

        if (
          platformResult.status ===
          'fulfilled'
        ) {
          const platformList =
            extractApiList(
              platformResult.value,
            );

          platformNotifications =
            platformList.map(
              (
                notification,
              ) => ({
                ...notification,
                source:
                  'platform',
                read:
                  Boolean(
                    notification.read_at,
                  ),
              }),
            );
        }

        if (
          ordersResult.status ===
          'fulfilled'
        ) {
          const orders =
            extractApiList(
              ordersResult.value,
            );

          orderNotifications =
            buildOrderNotifications(
              orders,
            );
        }

        const combined =
          [
            ...platformNotifications,
            ...orderNotifications,
          ].sort(
            (
              first,
              second,
            ) =>
              new Date(
                second.created_at ||
                  0,
              ).getTime() -
              new Date(
                first.created_at ||
                  0,
              ).getTime(),
          );

        setNotifications(
          combined,
        );
      } finally {
        if (silent) {
          setRefreshing(false);
        } else {
          setLoading(false);
        }
      }
    };

  useEffect(() => {
    fetchNotifications();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    establishmentId,
    userWorkflow,
    language,
  ]);

  const unreadCount =
    useMemo(() => {
      return notifications.filter(
        (notification) => {
          if (
            notification.source ===
            'platform'
          ) {
            return !notification.read_at;
          }

          return !notification.read;
        },
      ).length;
    }, [
      notifications,
    ]);

  const filteredNotifications =
    useMemo(() => {
      if (
        filter === 'unread'
      ) {
        return notifications.filter(
          (notification) => {
            if (
              notification.source ===
              'platform'
            ) {
              return !notification.read_at;
            }

            return !notification.read;
          },
        );
      }

      if (filter === 'qr') {
        return notifications.filter(
          (notification) =>
            notification.source ===
              'platform' &&
            notification?.data
              ?.kind ===
              'qr_print_recommendations_ready',
        );
      }

      return notifications;
    }, [
      filter,
      notifications,
    ]);

  const markPlatformRead =
    async (
      notificationId,
    ) => {
      const now =
        new Date().toISOString();

      setNotifications(
        (current) =>
          current.map(
            (notification) =>
              notification.id ===
              notificationId
                ? {
                    ...notification,
                    read_at:
                      notification.read_at ||
                      now,
                    read: true,
                  }
                : notification,
          ),
      );

      try {
        await notificationsApi.markRead(
          notificationId,
        );
      } catch {
        fetchNotifications(
          true,
        );
      }
    };

  const markOrderRead = (
    notificationId,
  ) => {
    setNotifications(
      (current) => {
        const next =
          current.map(
            (notification) =>
              notification.id ===
              notificationId
                ? {
                    ...notification,
                    read: true,
                  }
                : notification,
          );

        saveOrderReadState(
          next,
        );

        return next;
      },
    );
  };

  const markAllRead =
    async () => {
      const now =
        new Date().toISOString();

      setNotifications(
        (current) => {
          const next =
            current.map(
              (notification) => {
                if (
                  notification.source ===
                  'platform'
                ) {
                  return {
                    ...notification,
                    read_at:
                      notification.read_at ||
                      now,
                    read: true,
                  };
                }

                return {
                  ...notification,
                  read: true,
                };
              },
            );

          saveOrderReadState(
            next,
          );

          return next;
        },
      );

      try {
        await notificationsApi.markAllRead();
      } catch {
        fetchNotifications(
          true,
        );
      }
    };

  const openNotification =
    async (
      notification,
    ) => {
      if (
        notification.source ===
        'order'
      ) {
        markOrderRead(
          notification.id,
        );

        navigate(
          notification.link ||
            '/orders',
        );

        return;
      }

      await markPlatformRead(
        notification.id,
      );

      if (
        notification?.data
          ?.kind ===
        'qr_print_recommendations_ready'
      ) {
        const batchId =
          notification?.data
            ?.batch_id;

        if (batchId) {
          navigate(
            `/qr-print/recommendations/${batchId}`,
          );
        }
      }
    };

  return (
    <div className="notifications-page">
      <section className="notifications-page__hero">
        <div>
          <span className="notifications-page__eyebrow">
            <Bell size={15} />

            {t(
              'notifications.eyebrow',
            )}
          </span>

          <h1>
            {t(
              'notifications.title',
            )}
          </h1>

          <p>
            {t(
              'notifications.description',
            )}
          </p>
        </div>

        <div className="notifications-page__hero-actions">
          <button
            type="button"
            className="notifications-page__secondary-button"
            onClick={() =>
              fetchNotifications(
                true,
              )
            }
            disabled={
              refreshing
            }
          >
            {refreshing ? (
              <Loader2
                size={16}
                className="is-spinning"
              />
            ) : (
              <RefreshCw
                size={16}
              />
            )}

            {t(
              'common.refresh',
            )}
          </button>

          <button
            type="button"
            className="notifications-page__primary-button"
            onClick={
              markAllRead
            }
            disabled={
              unreadCount === 0
            }
          >
            <CheckCheck
              size={16}
            />

            {t(
              'notifications.markAllRead',
            )}
          </button>
        </div>
      </section>

      <section className="notifications-page__panel">
        <div className="notifications-page__toolbar">
          <div className="notifications-page__filters">
            {filters.map(
              (item) => (
                <button
                  key={
                    item.key
                  }
                  type="button"
                  className={
                    filter ===
                    item.key
                      ? 'is-active'
                      : ''
                  }
                  onClick={() =>
                    setFilter(
                      item.key,
                    )
                  }
                >
                  {
                    item.label
                  }
                </button>
              ),
            )}
          </div>

          <span className="notifications-page__count">
            {t(
              'notifications.unreadCount',
              {
                count:
                  unreadCount,
              },
            )}
          </span>
        </div>

        {loading ? (
          <div className="notifications-page__state">
            <Loader2
              size={28}
              className="is-spinning"
            />

            <strong>
              {t(
                'notifications.loading',
              )}
            </strong>
          </div>
        ) : filteredNotifications.length ===
          0 ? (
          <div className="notifications-page__state">
            <Bell
              size={30}
            />

            <strong>
              {t(
                'notifications.empty.title',
              )}
            </strong>

            <span>
              {t(
                'notifications.empty.description',
              )}
            </span>
          </div>
        ) : (
          <div className="notifications-page__list">
            {filteredNotifications.map(
              (
                notification,
              ) => {
                const isOrder =
                  notification.source ===
                  'order';

                const isQr =
                  notification
                    ?.data
                    ?.kind ===
                  'qr_print_recommendations_ready';

                const isUnread =
                  isOrder
                    ? !notification.read
                    : !notification.read_at;

                const status =
                  normalizeStatus(
                    notification.status,
                  );

                const NotificationIcon =
                  statusIcons[
                    status
                  ] ||
                  ShoppingBag;

                return (
                  <button
                    key={
                      notification.id
                    }
                    type="button"
                    className={`notifications-page__item ${
                      isUnread
                        ? 'is-unread'
                        : ''
                    } ${
                      isOrder
                        ? `notifications-page__item--${status}`
                        : ''
                    }`}
                    onClick={() =>
                      openNotification(
                        notification,
                      )
                    }
                  >
                    <span className="notifications-page__item-icon">
                      {isQr ? (
                        <Sparkles
                          size={20}
                        />
                      ) : isOrder ? (
                        <NotificationIcon
                          size={20}
                        />
                      ) : (
                        <Bell
                          size={20}
                        />
                      )}
                    </span>

                    <span className="notifications-page__item-content">
                      <strong>
                        {isQr
                          ? t(
                              'notifications.types.qrReady.title',
                            )
                          : isOrder
                            ? notification.title
                            : notification
                                ?.data
                                ?.title ||
                              t(
                                'notifications.defaultTitle',
                              )}
                      </strong>

                      <span>
                        {isQr
                          ? t(
                              'notifications.types.qrReady.message',
                            )
                          : isOrder
                            ? notification.message
                            : notification
                                ?.data
                                ?.message ||
                              ''}
                      </span>

                      <small>
                        {formatDateTime(
                          notification.created_at,
                        )}
                      </small>
                    </span>

                    {isUnread ? (
                      <span className="notifications-page__unread-dot" />
                    ) : null}
                  </button>
                );
              },
            )}
          </div>
        )}
      </section>
    </div>
  );
}